import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import { isObject, useFormik } from 'formik';
import FieldInput from '../../Components/FieldInput';
import FieldSelect from '../../Components/FieldSelect';
import API from '../../Common/API';
import { useSelector } from 'react-redux';
import TextEditor from '../../Components/Editor';
import { Dropzone, FileMosaic } from '@files-ui/react';

const initialProductForm = {
    productName: '',
    tProductName: '',
    brandName: '',
    tBrandName: '',
    category: '',
    subCategory: '',
    modelName: '',
    tModelName: '',
    productDescription: '',
    netQuantity: 0.0,
    netQuantityType: '',
    orderQuantity: 0,
    productImages: [],
    manufacturer: '',
    price: 0,
    cgst: 0,
    sgst: 0,
    offer: 0,
    productQuantity: 0,
    stockMode: '',
    deliveryCharges: 0,
    isOrganic: false,
    isHomemade: false,
    isPremium: false,
};

const stockModes = [
    { text: 'In Stock', value: 'inStock' },
    { text: 'Out Of Stock', value: 'outOfStock' },
    { text: 'Unavailable', value: 'unavailable' },
    { text: 'Coming Soon', value: 'comingSoon' },
];

const AddProduct = () => {
    const [fullCategory, setFullCategory] = useState([]);
    const [category, setCategory] = useState([]);
    const [subCategory, setSubCategory] = useState([]);
    const [files, setFiles] = React.useState([]);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const [submitDisabled, setSubmitDisabled] = useState(false);

    const MAX_BANNER_WIDTH = 1200;
    const MAX_BANNER_HEIGHT = 1200;
    const MIN_BANNER_HEIGHT = 600;
    const MIN_BANNER_WIDTH = 600;

    useEffect(() => {
        API.get('/category/completeCategories').then((catRes) => {
            if (catRes.status === 200) {
                console.log(catRes);
                setFullCategory(catRes.data.data);
                setCategory(
                    catRes.data.data.map((x) => {
                        return { text: x.category, value: x._id };
                    })
                );
            }
        });
    }, []);

    const productFormShcema = Yup.object().shape({
        productName: Yup.string().required('Product Name is required'),
        tProductName: Yup.string().nullable(),
        brandName: Yup.string().required('Brand Name is required'),
        tBrandName: Yup.string().nullable(),
        category: Yup.string().required('Category is required'),
        subCategory: Yup.string().required('Sub Category is required'),
        productDescription: Yup.string().nullable(),
        modelName: Yup.string().nullable(),
        tModelName: Yup.string().nullable(),
        // netQuantity: Yup.number(),
        // netQuantityType: Yup.string(),
        orderQuantity: Yup.number(),
        productImages: Yup.array(),
        manufacturer: Yup.string(),
        price: Yup.number(),
        cgst: Yup.number(),
        sgst: Yup.number(),
        productQuantity: Yup.number(),
        stockMode: Yup.string(),
        deliveryCharges: Yup.number(),
        isOrganic: Yup.boolean(),
        isHomemade: Yup.boolean(),
        isPremium: Yup.boolean(),
    });

    const productForm = useFormik({
        initialValues: initialProductForm,
        validateOnMount: true,
        validationSchema: productFormShcema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setSubmitDisabled(true);
            values.productImages = files;
            values.addedBy = userDetails.subject.sellerId._id
                ? userDetails.subject.sellerId._id
                : userDetails.subject.sellerId;
            const formData = new FormData();
            formData.append('productData', JSON.stringify(values));
            files.forEach((file) => {
                formData.append('files', file.file);
            });
            API.post('/products/addNewProduct', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }).then((prodRes) => {
                if (prodRes.status === 200) {
                    setFiles([]);
                    productForm.resetForm();
                    setSubmitDisabled(false);
                }
            });
        },
    });

    const selectSubCategory = (value) => {
        const selectCat = fullCategory.filter((x) => x._id === value)[0];
        setSubCategory(
            selectCat.subCategories.map((y) => {
                return { text: y.subCategory, value: y._id };
            })
        );
    };

    const updateFiles = async (incomingFiles) => {
        const validatedFiles = [];

        for (const file of incomingFiles) {
            const image = new Image();
            const objectUrl = URL.createObjectURL(file.file);

            const isValid = await new Promise((resolve) => {
                image.onload = () => {
                    const { width, height } = image;
                    const valid =
                        width >= MIN_BANNER_WIDTH &&
                        height >= MIN_BANNER_HEIGHT &&
                        width <= MAX_BANNER_WIDTH &&
                        height <= MAX_BANNER_HEIGHT;
                    resolve(valid);
                };
                image.onerror = () => resolve(false);
                image.src = objectUrl;
            });

            URL.revokeObjectURL(objectUrl); // clean up memory

            if (isValid) {
                validatedFiles.push(file);
            } else {
                alert(
                    `Image "${file.name}" must be between ${MIN_BANNER_WIDTH}x${MIN_BANNER_HEIGHT} and ${MAX_BANNER_WIDTH}x${MAX_BANNER_HEIGHT}px.`
                );
            }
        }

        setFiles(validatedFiles);
    };
    const removeFile = (id) => {
        setFiles(files.filter((x) => x.id !== id));
    };
    const translateFormHandling = async (text, fieldName) => {
        API.post('/common/translate', { text: text })
            .then((res) => {
                if (res.status === 200) {
                    const tText = res.data.data.translatedText;
                    productForm.setFieldValue(fieldName, tText); // Corrected this line
                }
            })
            .catch((error) => {
                console.error('Translation API Error:', error);
            });
    };

    return (
        <motion.div className="relative h-full w-full flex flex-col">
            <motion.div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-2 lg:grid-cols-2">
                <FieldInput
                    required={true}
                    label="Product Name"
                    fieldProps={productForm.getFieldProps('productName')}
                    fieldMeta={productForm.getFieldMeta('productName')}
                    fieldHelper={productForm.getFieldHelpers('productName')}
                    edit={true}
                    dataLoaded={true}
                    onChange={(e) => translateFormHandling(e, 'tProductName')}
                />
                <FieldInput
                    label="தயாரிப்பு பெயர்"
                    fieldProps={productForm.getFieldProps('tProductName')}
                    fieldMeta={productForm.getFieldMeta('tProductName')}
                    fieldHelper={productForm.getFieldHelpers('tProductName')}
                    edit={true}
                    dataLoaded={true}
                    onChange={(e) => translateFormHandling(e, 'tProductName')}
                />
                <FieldInput
                    required={true}
                    label="Brand Name"
                    fieldProps={productForm.getFieldProps('brandName')}
                    fieldMeta={productForm.getFieldMeta('brandName')}
                    fieldHelper={productForm.getFieldHelpers('brandName')}
                    edit={true}
                    dataLoaded={true}
                    onChange={(e) => translateFormHandling(e, 'tBrandName')}
                />
                <FieldInput
                    label="பிராண்ட் பெயர்"
                    fieldProps={productForm.getFieldProps('tBrandName')}
                    fieldMeta={productForm.getFieldMeta('tBrandName')}
                    fieldHelper={productForm.getFieldHelpers('tBrandName')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldSelect
                    required={true}
                    label="Category"
                    fieldProps={productForm.getFieldProps('category')}
                    fieldMeta={productForm.getFieldMeta('category')}
                    fieldHelper={productForm.getFieldHelpers('category')}
                    edit={true}
                    dataLoaded={true}
                    options={category}
                    onChange={(ev) => selectSubCategory(ev)}
                />
                <FieldSelect
                    required={true}
                    label="Sub Category"
                    fieldProps={productForm.getFieldProps('subCategory')}
                    fieldMeta={productForm.getFieldMeta('subCategory')}
                    fieldHelper={productForm.getFieldHelpers('subCategory')}
                    edit={true}
                    dataLoaded={true}
                    options={subCategory}
                />
                <FieldInput
                    label="Model Name"
                    fieldProps={productForm.getFieldProps('modelName')}
                    fieldHelper={productForm.getFieldHelpers('modelName')}
                    fieldMeta={productForm.getFieldMeta('modelName')}
                    edit={true}
                    dataLoaded={true}
                    onChange={(e) => translateFormHandling(e, 'tModelName')}
                />
                <FieldInput
                    label="மாதிரி பெயர்"
                    fieldProps={productForm.getFieldProps('tModelName')}
                    fieldHelper={productForm.getFieldHelpers('tModelName')}
                    fieldMeta={productForm.getFieldMeta('tModelName')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="Order Quantity"
                    fieldProps={productForm.getFieldProps('orderQuantity')}
                    fieldHelper={productForm.getFieldHelpers('orderQuantity')}
                    fieldMeta={productForm.getFieldMeta('orderQuantity')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="Manufacturer"
                    fieldProps={productForm.getFieldProps('manufacturer')}
                    fieldHelper={productForm.getFieldHelpers('manufacturer')}
                    fieldMeta={productForm.getFieldMeta('manufacturer')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="Price"
                    fieldProps={productForm.getFieldProps('price')}
                    fieldHelper={productForm.getFieldHelpers('price')}
                    fieldMeta={productForm.getFieldMeta('price')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="CGST"
                    fieldProps={productForm.getFieldProps('cgst')}
                    fieldHelper={productForm.getFieldHelpers('cgst')}
                    fieldMeta={productForm.getFieldMeta('cgst')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="SGST"
                    fieldProps={productForm.getFieldProps('sgst')}
                    fieldHelper={productForm.getFieldHelpers('sgst')}
                    fieldMeta={productForm.getFieldMeta('sgst')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="Offer"
                    fieldProps={productForm.getFieldProps('offer')}
                    fieldHelper={productForm.getFieldHelpers('offer')}
                    fieldMeta={productForm.getFieldMeta('offer')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldInput
                    label="Stock Quantity"
                    fieldProps={productForm.getFieldProps('productQuantity')}
                    fieldHelper={productForm.getFieldHelpers('productQuantity')}
                    fieldMeta={productForm.getFieldMeta('productQuantity')}
                    edit={true}
                    dataLoaded={true}
                />
                <FieldSelect
                    label="Stock Mode"
                    fieldProps={productForm.getFieldProps('stockMode')}
                    fieldHelper={productForm.getFieldHelpers('stockMode')}
                    fieldMeta={productForm.getFieldMeta('stockMode')}
                    edit={true}
                    dataLoaded={true}
                    options={stockModes}
                />
                {/* <FieldInput label="Delivery Charges" fieldProps={productForm.getFieldProps('deliveryCharges')} fieldHelper={productForm.getFieldHelpers('deliveryCharges')} fieldMeta={productForm.getFieldMeta('deliveryCharges')} edit={true} dataLoaded={true} /> */}
                <motion.div className="flex flex-row colspan-2 px-5 py-10">
                    <motion.div className="flex items-center w-full flex-row">
                        <input
                            type="checkbox"
                            checked={productForm.values.isOrganic}
                            className="cursor-pointer mx-1 w-5 h-5"
                            onClick={() =>
                                productForm.setValues({
                                    ...productForm.values,
                                    isOrganic: !productForm.values.isOrganic,
                                })
                            }
                        />
                        <label className="font-bold items-center flex flex-row w-full mx-2">
                            Organic
                        </label>
                    </motion.div>
                    <motion.div className="flex items-center w-full flex-row">
                        <input
                            type="checkbox"
                            checked={productForm.values.isHomemade}
                            className="cursor-pointer mx-1 w-5 h-5"
                            onClick={() =>
                                productForm.setValues({
                                    ...productForm.values,
                                    isHomemade: !productForm.values.isHomemade,
                                })
                            }
                        />
                        <label className="font-bold items-center flex flex-row w-full mx-2">
                            Homemade
                        </label>
                    </motion.div>
                    <motion.div className="flex items-center w-full flex-row">
                        <input
                            type="checkbox"
                            checked={productForm.values.isPremium}
                            className="cursor-pointer  mx-1 w-5 h-5"
                            onClick={() =>
                                productForm.setValues({
                                    ...productForm.values,
                                    isPremium: !productForm.values.isPremium,
                                })
                            }
                        />
                        <label className="font-bold  items-center flex flex-row w-full mx-2">
                            Premium
                        </label>
                    </motion.div>
                </motion.div>
            </motion.div>
            <motion.div className="grid gird-cols-1 gap-10 px-5">
                <motion.div className="flex flex-col w-full">
                    <motion.div className="text-md">Product Description</motion.div>
                    <TextEditor
                        onChange={(values) =>
                            productForm.setValues({
                                ...productForm.values,
                                productDescription: values,
                            })
                        }
                    />
                </motion.div>
                <motion.div className="flex flex-col w-full">
                    <motion.div className="flex flex-row justify-between">
                        <motion.div className="text-md">Product Images</motion.div>
                        <motion.div className="text-md font-bold">
                            Image size should be between 600pxx600px and 1200pxx1200px
                        </motion.div>
                    </motion.div>
                    <Dropzone
                        className="relative my-2"
                        onChange={updateFiles}
                        value={files}
                        accept="image/*"
                    >
                        {files.map((file) => (
                            <FileMosaic
                                preview={true}
                                key={file.id}
                                {...file}
                                onDelete={removeFile}
                                info
                            />
                        ))}
                    </Dropzone>
                </motion.div>
            </motion.div>
            <motion.div className="w-full flex flex-row justify-center">
                <motion.div className="bg-white/20 ring-1 ring-black mx-2 cursor-pointer rounded-full px-5 py-2 h-8 flex flex-row justify-center items-center">
                    Cancel
                </motion.div>
                <motion.div
                    className={`bg-primeColor/20 rounded-full mx-2 ${
                        submitDisabled || !productForm.isValid
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                    } px-5 py-2 h-8 flex flex-row justify-center items-center`}
                    onClick={() => {
                        if (!submitDisabled && productForm.isValid) {
                            productForm.submitForm();
                        }
                    }}
                    disabled={submitDisabled || !productForm.isValid}
                >
                    Submit
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default AddProduct;
